import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { Check, X, Shield } from "lucide-react";

const NotForEveryoneSection = () => {
  const { language } = useLanguage();
  
  const content = {
    sl: {
      title: "Nismo za vsakogar.",
      titleHighlight: "Takole preverite.",
      rightChoice: {
        label: "SMO PRAVA IZBIRA, ČE…",
        items: [
          "Želite računovodjo, ki vas opozori, preden nastane težava — ne šele po koncu leta",
          "Cenite jasne odgovore in fiksno mesečno ceno brez presenečenj",
          "Vodite s.p., d.o.o. ali podružnico — ne glede na dejavnost in velikost",
          "Želite vse na enem mestu: knjigovodstvo, DDV, plače in vso administracijo"
        ]
      },
      notForYou: {
        label: "NISMO ZA VAS, ČE…",
        items: [
          "Iščete zgolj najcenejšega knjigovodjo v mestu",
          "Želite le nekoga, ki vpisuje številke — brez nasveta in sodelovanja",
          "Vam je vseeno, če za težave izveste šele po koncu leta"
        ]
      },
      riskReversal: {
        title: "Menjava se zdi tvegana?",
        titleHighlight: "Mi poskrbimo, da ni.",
        intro: "Uredimo celotno predajo od prejšnjega računovodje, kadarkoli med letom — vi ne lovite niti enega dokumenta. Poleg tega se zavežemo:",
        points: [
          {
            bold: "Brez dolgoročne vezave.",
            text: "Pogodbo lahko odpoveste z 1-mesečnim odpovednim rokom, dokumentacijo pa vam ob odhodu predamo urejeno in brez doplačila."
          },
          {
            bold: "Odgovor v 1 delovnem dnevu.",
            text: "Na vsako vprašanje odgovorimo najkasneje naslednji delovni dan."
          }
        ]
      }
    },
    en: {
      title: "We're not for everyone.",
      titleHighlight: "Here's how to tell.",
      rightChoice: {
        label: "WE'RE THE RIGHT CHOICE IF…",
        items: [
          "You want an accountant who warns you before a problem happens — not after year-end",
          "You value clear answers and a fixed monthly fee with no surprises",
          "You run a sole proprietorship, a d.o.o. or a branch — any industry, any size",
          "You want everything in one place: bookkeeping, VAT, payroll and all the admin"
        ]
      },
      notForYou: {
        label: "WE'RE PROBABLY NOT FOR YOU IF…",
        items: [
          "You're shopping only for the cheapest bookkeeper in town",
          "You only want someone to key in numbers — no advice, no partnership",
          "You're happy to find out about problems after year-end"
        ]
      },
      riskReversal: {
        title: "Switching feels risky.",
        titleHighlight: "We make it painless.",
        intro: "We handle the entire handover from your previous accountant, at any point in the year — you don't chase a single document. On top of that, we commit to:",
        points: [
          {
            bold: "No long-term contract.",
            text: "Cancel with 1 month's notice, and we hand over all your records in order, at no extra charge."
          },
          {
            bold: "A reply within 1 working day.",
            text: "Every question answered by the next working day at the latest."
          }
        ]
      }
    }
  };
  
  const t = content[language] || content.sl;
  
  return (
    <section className="py-24 lg:py-32 px-6 lg:px-[120px] bg-[#F6F2EA]" data-testid="not-for-everyone-section">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-14">
        {/* Title */}
        <h2 className="font-serif text-3xl lg:text-[64px] leading-[1.02] tracking-[-1px] max-w-[900px] text-[#15181C] animate-on-scroll">
          {t.title} <span className="italic">{t.titleHighlight}</span>
        </h2>
        
        {/* Two column cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Right choice - Dark */}
          <div className="bg-[#0E1114] text-[#F4EFE6] p-10 lg:p-12 flex flex-col gap-6 animate-on-scroll from-left delay-1" data-testid="right-choice-card">
            <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462]">{t.rightChoice.label}</span>
            {t.rightChoice.items.map((item) => (
              <div key={item} className="flex gap-3.5 text-[17px] leading-[1.5]">
                <Check className="w-[22px] h-[22px] text-[#C9A462] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <span>{item}</span>
              </div>
            ))}
          </div>
          
          {/* Not for you - Light */}
          <div className="bg-[#FFFDF8] border border-[#E2DACB] p-10 lg:p-12 flex flex-col gap-6 animate-on-scroll from-right delay-2" data-testid="not-for-you-card">
            <span className="text-[13px] font-bold tracking-[2.5px] text-[#4E5359]">{t.notForYou.label}</span>
            {t.notForYou.items.map((item) => (
              <div key={item} className="flex gap-3.5 text-[17px] leading-[1.5] text-[#15181C]">
                <X className="w-[22px] h-[22px] text-[#4E5359] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Risk reversal band */}
        <div className="border border-[#C9A462] p-10 lg:p-14 bg-[#FFFDF8] flex flex-col lg:flex-row gap-8 lg:gap-10 items-start animate-on-scroll scale-up delay-3" data-testid="risk-reversal-band">
          {/* Shield icon */}
          <div className="w-[88px] h-[88px] flex-shrink-0 rounded-full bg-[#0E1114] text-[#C9A462] flex items-center justify-center">
            <Shield className="w-10 h-10" strokeWidth={1.5} />
          </div>
          
          {/* Content */}
          <div className="flex flex-col gap-2.5">
            <h3 className="font-serif text-2xl lg:text-[38px] leading-[1.05] text-[#15181C]">
              {t.riskReversal.title} <span className="italic">{t.riskReversal.titleHighlight}</span>
            </h3>
            <p className="text-base leading-[1.6] text-[#4E5359]">{t.riskReversal.intro}</p>
            <div className="flex flex-col gap-2.5 pt-1">
              {t.riskReversal.points.map((point) => (
                <div key={point.bold} className="flex gap-3 text-base leading-[1.55] text-[#15181C]">
                  <Check className="w-5 h-5 text-[#7A5B28] flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span><strong>{point.bold}</strong> {point.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotForEveryoneSection;
